import { useNavigate } from 'react-router-dom';
import { Badge, Button } from '../../../components/ui';
import { dateFilter, optionsFilter } from '../../../components/common/ListPage/listFilters';
import { Cell, ListPage } from '../../../components/common/ListPage/ListPage';
import { cn } from '../../../utils/cn';
import { labelOf } from '../../../utils/labels';
import { PAYMENT_MODES, TXN_CATEGORIES } from '../../../constants/payments';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import type { Transaction } from '../types/transactionTypes';

export function Amount({ t }: { t: Transaction }) {
  const inflow = t.direction === 'credit';
  return (
    <span className={cn('tabular font-semibold', inflow ? 'text-emerald-700' : 'text-navy-900', t.isReversed && 'line-through opacity-60')}>
      {inflow ? '+' : '−'} {money(t.amount)}
    </span>
  );
}

const REF_PATH = { invoice: '/invoices', purchase: '/purchases', expense: '/expenses', payslip: '/payroll' };

export default function TransactionList() {
  const navigate = useNavigate();
  const add = <Button leftIcon="plus" onClick={() => navigate('/transactions/new')}>Record payment</Button>;
  return (
    <ListPage
      title="Payments"
      subtitle="Every rupee in and out: customer payments, supplier payments, salaries, expenses"
      path="/transactions"
      filters={[
        dateFilter(),
        { key: 'direction', label: 'Direction', icon: 'swap', options: [{ value: 'credit', label: 'Money in' }, { value: 'debit', label: 'Money out' }] },
        optionsFilter('category', 'Type', TXN_CATEGORIES, 'grid'),
        optionsFilter('mode', 'Mode', PAYMENT_MODES, 'wallet'),
        { key: 'includeReversed', label: 'Reversed', icon: 'refresh', options: [{ value: 'true', label: 'Include reversed' }] },
      ]}
      searchPlaceholder="Number, party, UTR"
      actions={add}
      rowTo={(t) => `/transactions/${t._id}`}
      rowActions={(t) =>
        [
          { label: 'View', icon: 'eye', onClick: () => navigate(`/transactions/${t._id}`) },
          REF_PATH[t.refType] && t.refId && { label: `Open ${t.refType === 'payslip' ? 'salary slip' : t.refType}`, icon: 'receipt', onClick: () => navigate(`${REF_PATH[t.refType]}/${t.refId}`) },
        ].filter(Boolean)
      }
      columns={[
        { header: 'Number', sort: 'transactionNo', sortDesc: true, render: (t) => t.transactionNo, csv: (t) => t.transactionNo },
        { header: 'Date', sort: 'date', sortDesc: true, render: (t) => formatDate(t.date), csv: (t) => formatDate(t.date) },
        { header: 'Party', sort: 'partyName', render: (t) => <Cell title={t.partyName || '-'} sub={t.note} />, csv: (t) => t.partyName },
        { header: 'Type', sort: 'category', render: (t) => <Badge tone={t.direction === 'credit' ? 'green' : 'neutral'}>{labelOf(TXN_CATEGORIES, t.category)}</Badge>, csv: (t) => labelOf(TXN_CATEGORIES, t.category) },
        { header: 'Mode', sort: 'mode', render: (t) => <>{labelOf(PAYMENT_MODES, t.mode)}{t.referenceNo && <span className="block text-xs text-ink-400">{t.referenceNo}</span>}</>, csv: (t) => labelOf(PAYMENT_MODES, t.mode) },
        { header: 'Reference', sort: 'referenceNo', render: (t) => t.referenceNo || '-', csv: (t) => t.referenceNo, defaultHidden: true },
        { header: 'Amount', sort: 'amount', sortDesc: true, align: 'right', render: (t) => <Amount t={t} />, csv: (t) => (t.direction === 'credit' ? 1 : -1) * (t.amount / 100) },
      ]}
      mobile={(t) => ({
        title: t.partyName || labelOf(TXN_CATEGORIES, t.category),
        subtitle: [formatDate(t.date), labelOf(PAYMENT_MODES, t.mode), t.transactionNo].join(' · '),
        right: <Amount t={t} />,
      })}
      empty={{ icon: 'wallet', title: 'No payments yet', message: 'Record money received from customers and paid to suppliers.', action: add }}
    />
  );
}
